<template>
  <div class="mt-4" :id="id">
    <h3 class="text-3xl font-bold tracking-tight mb-2">
      {{ title }}
    </h3>
    <p class="text-sm text-gray-600 mb-2" v-if="recentEditDate">
      Most recent edit: {{ formatDate(recentEditDate) }}
    </p>
    <p class="text-sm font-semibold text-yellow-700 mb-6" v-if="note">
      {{ note }}
    </p>
    <div class="overflow-x-auto w-full">
      <table class="schedule-table w-full min-w-[700px] text-center">
        <thead>
          <tr>
            <th
              v-for="day in daysOfWeek"
              :key="day"
              class="border border-gray-800 p-4 text-center bg-gray-900 text-white"
            >
              {{ capitalize(day) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(timeSlot, timeIndex) in maxTimeSlots" :key="`row-${timeIndex}`">
            <td
              v-for="day in daysOfWeek"
              :key="`${day}-${timeIndex}`"
              class="px-4 py-4 border"
              :class="getCellClass(day, timeIndex)"
            >
              <div v-if="getClassForDayAndTime(day, timeIndex)">
                <div class="font-bold">{{ getClassForDayAndTime(day, timeIndex).title }}</div>
                <div class="text-sm">{{ formatTime(getClassForDayAndTime(day, timeIndex)) }}</div>
                <div v-if="getClassForDayAndTime(day, timeIndex).coach && getClassForDayAndTime(day, timeIndex).coach.length > 0" class="text-xs text-gray-600">
                  ({{ getClassForDayAndTime(day, timeIndex).coach.join(', ') }})
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import scheduleData from '../../data/schedules.json'

export default {
  name: 'ScheduleCategory',
  props: {
    title: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    scheduleType: {
      type: String,
      default: 'uncategorized',
      validator(value) {
        return ['kids', 'womens', 'mens', 'uncategorized'].includes(value)
      }
    },
    location: {
      type: String,
      required: true
    },
    note: {
      type: String,
      default: null
    }
  },
  computed: {
    daysOfWeek() {
      return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    },
    scheduleData() {
      const locationData = scheduleData.schedule?.[this.location] || {}
      const categoryKey = locationData[this.scheduleType] ? this.scheduleType : 'uncategorized'
      const rawData = locationData[categoryKey]?.days || {}
      
      const sortedData = {}
      
      // Sort each day's classes by timeStart
      for (const day in rawData) {
        if (rawData[day] && Array.isArray(rawData[day])) {
          sortedData[day] = [...rawData[day]].sort((a, b) => {
            const timeA = a.timeStart || '00:00'
            const timeB = b.timeStart || '00:00'
            return timeA.localeCompare(timeB)
          })
        } else {
          sortedData[day] = rawData[day]
        }
      }
      
      return sortedData
    },
    maxTimeSlots() {
      const lengths = this.daysOfWeek.map(day => {
        const dayClasses = this.scheduleData[day] || []
        return Array.isArray(dayClasses) ? dayClasses.length : 0
      })
      const maxSlots = lengths.length > 0 ? Math.max(...lengths) : 0
      return maxSlots > 0 ? Array.from({ length: maxSlots }, (_, i) => i) : []
    },
    recentEditDate() {
      const locationData = scheduleData.schedule?.[this.location] || {}
      const categoryKey = locationData[this.scheduleType] ? this.scheduleType : 'uncategorized'
      return locationData[categoryKey]?.recentEditDate
    }
  },
  methods: {
    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    getClassForDayAndTime(day, timeIndex) {
      const dayClasses = this.scheduleData[day] || []
      if (!Array.isArray(dayClasses) || typeof timeIndex !== 'number') {
        return null
      }
      return dayClasses[timeIndex] || null
    },
    formatTime(classData) {
      if (!classData) return ''
      const startTime = this.formatTimeWithPM(classData.timeStart)
      const endTime = this.formatTimeWithPM(classData.timeEnd)
      return `${startTime}-${endTime}`
    },
    formatTimeWithPM(time) {
      if (!time) return ''
      const [hours, minutes] = time.split(':')
      const hourNumber = parseInt(hours, 10)
      const timeDisplay = minutes === '00' ? '' : `:${minutes}`
      
      if (hourNumber >= 13) {
        const convertedHour = hourNumber - 12
        return `${convertedHour}${timeDisplay}PM`
      } else if (hourNumber === 12) {
        return `12${timeDisplay}PM`
      } else if (hourNumber === 0) {
        return `12${timeDisplay}AM`
      } else {
        return `${hourNumber}${timeDisplay}AM`
      }
    },
    getCellClass(day, timeIndex) {
      const classData = this.getClassForDayAndTime(day, timeIndex)
      if (!classData || !classData.title) {
        return 'bg-gray-900'
      }
      return 'bg-white text-black'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.schedule-table {
  @apply bg-white;
}

.schedule-table td:empty {
  background-color: rgb(17, 17, 17);
}
</style>

<style>
/* Global styles for location pages using ScheduleCategory */
.location-block {
  @apply bg-gray-50 p-8 rounded-lg shadow-sm;
}
</style>